/**
 * 무대 바닥의 크기.
 *
 * 네 곳이 이 값에 묶여 있어서 한 군데 모아 둡니다.
 *   - StudioEnvironment: 실제 바닥 원판을 이 반경으로 그립니다.
 *   - DrivingGround: 주행 노면과 경계 표식을 이 값으로 배치합니다.
 *   - vehicleDriving: 리스폰 반경이 노면 안에 있어야 합니다. 넘으면 차가 허공을 달립니다.
 *   - ShowroomCanvas: 카메라 far plane이 노면 지름을 담아야 합니다.
 *
 * 예전에는 바닥 반경이 StudioEnvironment에만 상수로 있었습니다. 주행이 들어오면서
 * 두 값이 서로를 알아야 하는데, 각자 상수를 들고 있으면 한쪽만 고쳤을 때 차가 바닥
 * 밖으로 나가고 그게 화면을 봐야만 드러납니다.
 */

/** 보기 모드의 스튜디오 바닥 원판 반경 (m). */
export const FLOOR_RADIUS = 14;

/**
 * 주행 코스의 반경 (m). 바깥 적백 커브와 흰 경계선이 놓이는 자리입니다.
 *
 * 외곽 트랙 중앙은 1,188m 반경이라 가장 빠른 Model S Plaid가 322km/h로 돌아도
 * 필요한 횡가속도는 약 6.7m/s²입니다. 조향 그립 한계(9m/s²)보다 낮아 모든 차종이
 * 최고속도로 트랙을 돌 수 있습니다. 추격 카메라가 차량을 따라가므로 맵을 이만큼
 * 키워도 화면 속 차량 크기는 바뀌지 않습니다.
 */
export const DRIVING_YARD_RADIUS = 1200;

/** 고속에서 조향을 보정할 여유를 둔 외곽 트랙 폭 (m). */
export const DRIVING_TRACK_WIDTH = 24;
export const DRIVING_TRACK_INNER_RADIUS = DRIVING_YARD_RADIUS - DRIVING_TRACK_WIDTH;
export const DRIVING_TRACK_CENTER_RADIUS =
  (DRIVING_TRACK_INNER_RADIUS + DRIVING_YARD_RADIUS) / 2;

/** 출발선은 트랙의 +X 쪽을 가로지르고, 차량은 4m 전에서 -Z 방향으로 출발합니다. */
export const DRIVING_START_LINE_X = DRIVING_TRACK_CENTER_RADIUS;
export const DRIVING_START_LINE_Z = 0;
export const DRIVING_SPAWN_X = DRIVING_START_LINE_X;
export const DRIVING_SPAWN_Z = DRIVING_START_LINE_Z + 4;
export const DRIVING_SPAWN_HEADING = Math.PI / 2;

/**
 * 커브 밖 이탈 허용 폭 (m).
 *
 * 커브를 넘는 순간 리스폰하면 두 가지가 나빠집니다. 커브를 스치면서 도는 정상적인
 * 주행이 초기화되고, 초기화가 너무 빨라서 사용자가 자기가 코스를 벗어났다는 것을
 * 볼 시간이 없습니다(그러면 원인 모를 순간이동으로 읽힙니다).
 *
 * 36m는 가장 빠른 322km/h에서도 약 0.4초입니다. 화면이 어두워지고 안내가 뜨는 것을
 * 볼 수 있을 만큼은 되고, 벌칙이 답답할 만큼 길지는 않습니다. 저속으로 흘러 나가면
 * 그만큼 더 여유가 생기는데, 천천히 벗어나는 쪽이 대개 실수라서 그 방향이 맞습니다.
 */
export const DRIVING_RUNOFF_WIDTH = 36;

/**
 * 차량 원점이 이 반경을 넘으면 트랙 출발선으로 되돌아갑니다 (m).
 *
 * 예전에는 여기에 보이지 않는 벽이 있어서 차를 되밀었습니다. 벽을 없앤 이유는 그
 * 동작이 무대의 나머지와 어긋났기 때문입니다 — 커브는 노면 끝을 뜻하는 표식인데,
 * 거기서 차가 멈춰 서면 커브가 아니라 담장이 됩니다. 게다가 정면으로 붙으면 조향만
 * 으로는 나올 수 없어서(실차와 같습니다) 후진을 모르는 사용자가 갇혔습니다.
 *
 * 이제 커브를 넘어가는 것은 허용하고, 이탈 구간을 지나면 출발선으로 되돌립니다.
 */
export const DRIVING_RESPAWN_RADIUS = DRIVING_YARD_RADIUS + DRIVING_RUNOFF_WIDTH;

/**
 * 주행 모드의 노면 반경 (m).
 *
 * 보기 모드와 나눠 둡니다. 쇼룸의 14m 원판은 차 한 대를 세워 두고 둘러보는 크기라서
 * 주행에는 턱없이 좁습니다. 반대로 이 크기를 보기 모드에도 쓰면 스튜디오 프리셋의
 * "호리존트" 느낌(바닥 끝이 배경으로 자연스럽게 넘어가는 것)이 달라지고, 그건 지금
 * 조명·반사와 함께 맞춰 놓은 것이라 건드릴 이유가 없습니다.
 *
 * 리스폰 반경보다 16m 크게 둡니다. 여유가 아니라 **카메라가 설 자리**입니다. 추격
 * 카메라는 차량 원점보다 최대 6.85m 뒤에 서고, 가장 긴 차의 앞 오버행도 2.9m라
 * 16m 안에 함께 들어옵니다. 노면이 더 좁으면 이탈 직전 카메라와 차 사이로 노면
 * 경계가 지나가며 아스팔트가 허공에서 끊겨 보입니다.
 */
export const DRIVING_FLOOR_RADIUS = DRIVING_RESPAWN_RADIUS + 16;
